<template>
    <div class="private-layout">
        <v-navigation-drawer v-model="drawer" app :permanent="!isMobile" :temporary="isMobile" width="250"
            color="#1E1E2D" dark class="sidebar" floating>
            <!-- Logo -->
            <div class="d-flex align-center px-5" style="height: 64px;">
                <router-link to="/home" style="text-decoration: none;">
                    <v-img :src="logoUrl" alt="Logo" max-height="38" max-width="160" contain
                        class="sidebar-logo-img"></v-img>
                </router-link>
            </div>

            <!-- Menu groups -->
            <v-list dense nav class="px-3 pt-0">
                <div v-if="availableClients.length" class="sidebar-client-context px-2 py-2">
                    <div class="sidebar-context-label">Caso em gestão</div>
                    <v-select
                        v-if="availableClients.length > 1"
                        v-model="selectedClientId"
                        :items="availableClients"
                        item-text="name"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        class="sidebar-client-select"
                        @change="changeSelectedClient"
                    ></v-select>
                    <div v-else class="sidebar-client-name text-truncate">
                        {{ availableClients[0].name }}
                    </div>
                </div>

                <template v-for="(menuGroup, groupIndex) in sidebarMenu">
                    <template v-if="isGroupVisible(menuGroup)">
                        <div :key="'label-' + groupIndex" class="sidebar-group-label px-2 pt-4 pb-2">{{ menuGroup.name }}
                        </div>
                        <template v-for="(menuOption, menuIndex) in menuGroup.children">
                            <MenuItem v-if="!menuGroup.path || $check(menuOption.path, 'GET')" :item="menuOption"
                                :key="'item-' + groupIndex + '-' + menuIndex">
                            </MenuItem>
                        </template>
                    </template>
                </template>
            </v-list>

            <!-- User at bottom -->
            <template v-slot:append>
                <div class="sidebar-user-section px-4 py-3">
                    <div class="d-flex align-center">
                        <v-avatar size="36" class="mr-3">
                            <img :src="'https://ui-avatars.com/api/?name=' + (user ? user.name : '')" />
                        </v-avatar>
                        <div class="flex-grow-1" style="min-width: 0;">
                            <div class="sidebar-user-name text-truncate">{{ user ? user.name : '' }}</div>
                            <div class="sidebar-user-email text-truncate">{{ user ? user.email : '' }}</div>
                        </div>
                        <v-btn icon small @click="logout" class="ml-1">
                            <v-icon size="18" color="#565674">mdi-logout</v-icon>
                        </v-btn>
                    </div>
                </div>
            </template>
        </v-navigation-drawer>

        <!-- Mobile top bar -->
        <v-app-bar v-if="isMobile" app color="#FFFFFF" elevation="0" height="56"
            style="border-bottom: 1px solid #E5E7EB;">
            <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
            <v-spacer></v-spacer>
            <router-link to="/home" style="text-decoration: none;">
                <v-img :src="logoUrl" alt="Logo" max-height="32" max-width="120" contain></v-img>
            </router-link>
            <v-spacer></v-spacer>
            <v-avatar size="32">
                <img :src="'https://ui-avatars.com/api/?name=' + (user ? user.name : '')" />
            </v-avatar>
        </v-app-bar>

        <v-main class="main-content">
            <router-view></router-view>
        </v-main>
    </div>
</template>

<script>
import { eventHub } from "@/main";
import router from '../router';
import axios from "axios";

import MenuItem from "@/layouts/components/MenuItem";
import profileMenu from "@/common/data/profile-menu.data"
import sidebarMenu from "@/common/data/sidebar-menu.data"

export default {
    name: "PrivateLayout",

    components: {
        MenuItem
    },

    data() {
        return {
            logoUrl: require('@/assets/logo-bej.svg'),
            menuGroups: [],
            drawer: true,
            searchTerm: null,
            user: null,
            displaySettingsMenu: false,
            sidebarMenu: sidebarMenu,
            profileMenu: profileMenu,
            selectedClientId: null,
            // Recursos ativos no caso selecionado (grupos do menu com `feature`).
            features: { rma: false },
        };
    },

    computed: {
        availableClients() {
            if (!this.user) return [];

            if (this.user.clients && this.user.clients.length) {
                return this.user.clients;
            }

            if (this.user.client) {
                return [this.user.client];
            }

            if (this.user.client_id) {
                return [{ id: this.user.client_id, name: this.user.client_names || 'Caso vinculado' }];
            }

            return [];
        },

        routeName() {
            return this.$route.name;
        },
        isMobile() {
            return this.$vuetify.breakpoint.smAndDown;
        }
    },

    watch: {
        isMobile(val) {
            this.drawer = !val;
        }
    },

    async created() {
        this.user = this.$root.user;
        this.setInitialClient();
        this.drawer = !this.isMobile;
        const initialClientId = this.selectedClientId;
        this.loadFeatures();
        eventHub.$on("client-context-changed", this.loadFeatures);
        await this.loadProfile();
        // O perfil pode trocar o caso selecionado (caso salvo que nao e mais permitido).
        if (this.selectedClientId !== initialClientId) this.loadFeatures();

        eventHub.$on("close-menu", () => {
            if (this.isMobile) {
                this.drawer = false;
            }
        });

        eventHub.$on("logout", () => {
            this.logout();
        });

        this.setActionButtons();
    },

    destroyed() {
        eventHub.$off("THEME_UPDATED");
        eventHub.$off("close-menu");
        eventHub.$off("logout");
        eventHub.$off("client-context-changed", this.loadFeatures);
    },

    methods: {
        setInitialClient() {
            const clients = this.availableClients;
            const currentClientId = Number(localStorage.getItem("selectedClientId"));
            const allowedClientIds = clients.map((client) => Number(client.id));
            const fallbackClientId = allowedClientIds[0] || (this.user && this.user.client_id);

            this.selectedClientId = allowedClientIds.includes(currentClientId)
                ? currentClientId
                : Number(fallbackClientId || null);

            if (this.selectedClientId) {
                localStorage.setItem("selectedClientId", String(this.selectedClientId));
            }
        },

        async loadProfile() {
            try {
                const result = await axios.get("/api/admin/client/profile");
                const profile = result.data.content;

                this.user = profile;
                this.$root.user = profile;
                localStorage.setItem("user", JSON.stringify({ data: profile }));
                this.setInitialClient();
            } catch (error) {
                // O token ainda controla o acesso; se o perfil falhar, o interceptor direciona o fluxo.
            }
        },

        isGroupVisible(menuGroup) {
            if (menuGroup.path && !this.$check(menuGroup.path, 'GET')) return false;
            if (menuGroup.feature && !this.features[menuGroup.feature]) return false;
            return menuGroup.children.length > 0;
        },

        // Fiscalizacao mensal (RMA): o grupo so aparece quando o caso tem o RMA ativo.
        async loadFeatures() {
            this.featuresRequest = (this.featuresRequest || 0) + 1;
            const request = this.featuresRequest;
            let enabled = false;
            try {
                const result = await axios.get("/api/client/rma/summary");
                enabled = !!(result.data.content && result.data.content.enabled);
            } catch (error) {
                enabled = false;
            }
            if (request === this.featuresRequest) this.features = { ...this.features, rma: enabled };
        },

        changeSelectedClient() {
            if (!this.selectedClientId) return;

            localStorage.setItem("selectedClientId", String(this.selectedClientId));
            eventHub.$emit("client-context-changed", this.selectedClientId);
            router.go(0);
        },

        setActionButtons() {
            // Grupo
        },

        truncateText(text, maxLength) {
            if (!text) return '';
            return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
        },

        menuActionClick(event) {
            event.preventDefault()
        },

        async logout() {
            localStorage.clear();
            router.push("/login");
        },
    },
};
</script>

<style scoped lang="scss">
.private-layout {
    height: 100%;
}

.sidebar {
    border-right: none !important;
    border-radius: 0 !important;
}

.sidebar-logo-img {
    filter: brightness(0) invert(1);
}

.sidebar-group-label {
    font-size: 10px;
    font-weight: 700;
    color: #565674;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

.sidebar-client-context {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.sidebar-context-label {
    font-size: 10px;
    font-weight: 700;
    color: #565674;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 8px;
}

.sidebar-client-name {
    color: #E5E7EB;
    font-size: 12px;
    font-weight: 600;
}

.sidebar-client-select ::v-deep .v-input__slot {
    min-height: 38px !important;
    background: rgba(255, 255, 255, 0.06) !important;
}

.sidebar-client-select ::v-deep input,
.sidebar-client-select ::v-deep .v-select__selection {
    color: #E5E7EB !important;
    font-size: 12px;
}

.sidebar-user-section {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.sidebar-user-name {
    font-size: 13px;
    font-weight: 500;
    color: #E5E7EB;
    line-height: 1.3;
}

.sidebar-user-email {
    font-size: 11px;
    color: #565674;
    line-height: 1.3;
}

.main-content {
    background-color: #EEEEF3 !important;
}
</style>
